import { useState } from 'react';
import {
  ILicencesTemplateResponseContent,
  ILicenceTemplateFormValues,
  ILicenceTemplateApplicationProcessResponse,
  ILicenceTemplateProcessTypes,
  ILicenceTemplateResponse,
  ISearchForm,
  ILicenceTemplateApplicationProcessSettingsUpdate,
  ILicenceTemplateApplicationProcessPriceUpdate,
  ILicenceTemplateApplicationProcess,
  ILicenceTemplateApplicationProcessTaskRequest,
  ILicenceTemplateApplicationProcessTask,
  ILicenceTemplateApplicationProcessDocument,
  ILicenceTemplateApplicationProcessDocumentRequest,
  IProcessTypes,
  ILicenceTemplateApplicationProcessIntelligenceReportRequest,
  ILicenceTemplateApplicationProcessIntelligenceReport,
  ILicenceTemplateApplicationProcessIntelligenceReportForm,
  IPrintoutSettings,
} from 'models/licenceConfigurator';
import { EDictionaryTypes } from 'models/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useTranslations } from 'hooks/useTranslations';
import {
  changeLicenceTemplateStatus,
  createLicenceTemplate,
  deleteLicence,
  editLicenceTemplateData,
  editLicenceTemplateApplicationProcessPriceData,
  getLicenceTemplate,
  getLicenceTemplatePriceApplicationProccessDetail,
  getLicenceTemplateApplicationProccess,
  getLicenceTemplateProcessTypes,
  getLicenceTemplates,
  deleteLicenceApplicationProcess,
  createLicenceTemplateApplicationProcess,
  editLicenceTemplateApplicationProcessSettingsData,
  deactivateApplicationProccess,
  activateApplicationProccess,
  createApplicationProccessTask,
  getApplicationProccessTask,
  deleteApplicationProccessTask,
  createApplicationProccessDocument,
  deleteApplicationProccessDocument,
  getApplicationProccessDocument,
  createApplicationProccessIntelligenceReport,
  deleteApplicationProccessIntelligenceReport,
  getApplicationProccessIntelligenceReport,
  updateApplicationProccessIntelligenceReport,
  updatePrintoutSettings,
  createPrintoutSettings,
  getPrintoutSettingsAvailableFields,
} from 'requests/licenceConfigurator';
import { EAlertVariants } from 'components/atoms/Alert';
import { EPerPages, ESortDirectionTypes, ISortOptions } from 'models/table';
import { IPaginator } from 'models/paginator';
import { IFormElementOptionProp } from 'models/form';
import {
  printoutSettingsConditionsFields,
  printoutSettingsFieldFilter,
  printoutSettingsGeneralFields,
  printoutSettingsOwnerFields,
  printoutSettingsWeaponDataFields,
} from './helper';

export const useConfigurationLicence = () => {
  const { setAlert } = useAlert();
  const { t } = useTranslations();
  const [paginator, setPaginator] = useState<IPaginator | null>(null);
  const [paginatorApplicationProccessTasks, setPaginatorApplicationProccessTasks] =
    useState<IPaginator | null>(null);
  const [
    paginatorApplicationProccessIntelligenceReports,
    setPaginatorApplicationProccessIntelligenceReports,
  ] = useState<IPaginator | null>(null);
  const [paginatorApplicationProccessDocuments, setPaginatorApplicationProccessDocuments] =
    useState<IPaginator | null>(null);
  const [licences, setLicences] = useState<ILicencesTemplateResponseContent[] | null>(null);
  const [licence, setLicence] = useState<ILicenceTemplateResponse | null>(null);
  const [licencePrices, setLicencePrices] = useState<
    ILicenceTemplateApplicationProcessResponse[]
  >([]);
  const [licenceApplicationProccess, setLicenceApplicationProccess] =
    useState<ILicenceTemplateApplicationProcessResponse | null>(null);
  const [applicationProccessTasks, setApplicationProccessTasks] = useState<
    ILicenceTemplateApplicationProcessTask[]
  >([]);
  const [applicationProccessIntelligenceReports, setApplicationProccessIntelligenceReports] =
    useState<ILicenceTemplateApplicationProcessIntelligenceReport[]>([]);
  const [applicationProccessDocuments, setApplicationProccessDocuments] = useState<
    ILicenceTemplateApplicationProcessDocument[]
  >([]);
  const [licenceProcessTypes, setLicenceProcessTypes] = useState<IFormElementOptionProp[]>([]);
  const [generalFields, setGeneralFields] = useState<string[]>([]);
  const [conditionsFields, setConditionsFields] = useState<string[]>([]);
  const [ownerFields, setOwnerFields] = useState<string[]>([]);
  const [weaponDataFields, setWeaponDataFields] = useState<string[]>([]);
  const {
    getWeaponLicenceCategory,
    weaponLicenceCategory,
    getWeaponLicenceCompetency,
    weaponLicenceCompetency,
    getWeaponLicenceGoodReason,
    weaponLicenceGoodReason,
    getWeaponLicencePaymentStatus,
    weaponLicencePaymentStatus,
    weaponLicenceStatus,
    getWeaponLicenceStatus,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    region,
    country,
    addressType,
    getRegion,
    getCountry,
    getAddressType,
    organisationStateType,
    getOrganisationStateType,
    organisationCivilianType,
    getOrganisationCivilianType,
    organisationCategory,
    getOrganisationCategory,
    weaponLicenceCondition,
    getWeaponLicenceCondition,
    attachmentDocumentTypeData,
    getAttachmentDocumentTypeData,
    applicationProcessTypes,
    getApplicationProcessTypes,
    organisationMilitaryType,
    getOrganisationMilitaryType,
    organisationPoliceType,
    getOrganisationPoliceType,
    applicationProcessStatus,
    getApplicationProcessStatus,
    getWeaponLicenceTemplateStatus,
    weaponLicenceTemplateStatus,
    legalType,
    getLegalType,
    task,
    getTask,
    otherRequestTaskName,
    getOtherRequestTaskName,
    weaponLicenceCaseStatus,
    getWeaponLicenceCaseStatus,
    intelligenceReportSourceSystem,
    getIntelligenceReportSourceSystem,
  } = useDictionary();

  const getLicence = async (id: string): Promise<ILicenceTemplateResponse | null> => {
    const data = await getLicenceTemplate(id);
    if (data) {
      setLicence(data);
      return data;
    }
    setLicence(null);
    return null;
  };

  const getLicencesData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    query: Partial<ISearchForm> = {},
    sort?: ISortOptions | null,
  ): Promise<ILicenceTemplateResponse | null> => {
    const data = await getLicenceTemplates(
      currentPage,
      perPage,
      query,
      sort ?? {
        property: 'createdDate',
        direction: ESortDirectionTypes.DESC,
      },
    );
    if (data) {
      const { content, ...paginatorData } = data;
      setLicences(content);
      setPaginator(paginatorData);
    } else {
      setLicences([]);
      setPaginator(null);
    }
    return null;
  };

  const getLicenceApplicationProccessData = async (
    id: string,
  ): Promise<ILicenceTemplateApplicationProcessResponse[] | null> => {
    const data = await getLicenceTemplateApplicationProccess(id);
    if (data) {
      const mapped = data.map((item) => ({
        ...item,
        priceFee: item.priceFee.toString().replace('.', ','),
      }));
      setLicencePrices(mapped);
      return mapped;
    }
    setLicencePrices([]);
    return null;
  };

  const getLicenceApplicationProccessDetail = async (
    id: string,
  ): Promise<ILicenceTemplateApplicationProcessResponse | null> => {
    const data = await getLicenceTemplatePriceApplicationProccessDetail(id);
    if (data) {
      const mapped = { ...data, priceFee: data.priceFee.toString().replace('.', ',') };
      setLicenceApplicationProccess(mapped);
      return mapped;
    }
    setLicenceApplicationProccess(null);
    return null;
  };

  const getLicenceApplicationProcessTypes = async (
    id: string,
  ): Promise<ILicenceTemplateProcessTypes | null> => {
    const data = await getLicenceTemplateProcessTypes(id);
    if (data?.processTypes) {
      setLicenceProcessTypes(
        data.processTypes.map((option: IProcessTypes) => ({
          label: option.name,
          value: option.uniqueId,
        })),
      );
      return data;
    }
    setLicenceProcessTypes([]);
    return null;
  };

  const getTrueKeys = (obj: { [key: string]: boolean }): string[] =>
    Object.keys(obj).filter((key) => obj[key]);

  const prepareFormDataToSave = (data: ILicenceTemplateFormValues) => {
    const legalTypesKeys: any[] = getTrueKeys(data.owner.legalTypes);
    const goodReasonDictionaryIds: any[] = getTrueKeys(
      data.conditions.goodReasonDictionaryIds,
    );

    const conditionsValues = {
      goodReason:
        data.conditions.goodReason && data.conditions.goodReason !== 'false'
          ? data.conditions.goodReason
          : null,
      goodReasonDictionaryIds,
      specificCountryArea: data.conditions.specificCountryArea,
      competency: data.conditions.competency,
      safeStorage:
        data.conditions.safeStorage && data.conditions.safeStorage !== 'false'
          ? data.conditions.safeStorage
          : null,
      weaponCategory: data.conditions.weaponCategory,
      weaponNumberLimit: data.conditions.weaponNumberLimit,
      carryConditions: data.conditions.carryConditions,
      additionalConditions: data.conditions.additionalConditions,
    };
    const generalValues = {
      ...data.general,
    };

    const organisationValues = data.owner.legalTypes.ORGANIZATION
      ? {
          legalEntityId: data.owner.organisation.legalEntityId,
          organisationName: data.owner.organisation.organisationName,
          primaryAddress: data.owner.organisation.primaryAddress,
          organisationType: data.owner.organisation.organisationType,
          departmentName: data.owner.organisation.departmentName,
        }
      : {
          legalEntityId: false,
          organisationName: false,
          primaryAddress: false,
          organisationType: false,
          departmentName: false,
        };

    const individualValues = data.owner.legalTypes.INDIVIDUAL
      ? {
          legalEntityId: data.owner.individual.legalEntityId,
          fullName: data.owner.individual.fullName,
          primaryAddress: data.owner.individual.primaryAddress,
          gender: data.owner.individual.gender,
          documentTypeAndNumber: data.owner.individual.documentTypeAndNumber,
          dateOfBirth: data.owner.individual.dateOfBirth,
          placeOfBirth: data.owner.individual.placeOfBirth,
          nationality: data.owner.individual.nationality,
          height: data.owner.individual.height,
          occupation: data.owner.individual.occupation,
          secondSurname: data.owner.individual.secondSurname,
          ageLimit: data.owner.individual.ageLimit,
        }
      : {
          legalEntityId: false,
          fullName: false,
          primaryAddress: false,
          gender: false,
          documentTypeAndNumber: false,
          dateOfBirth: false,
          placeOfBirth: false,
          nationality: false,
          height: false,
          occupation: false,
          secondSurname: false,
          ageLimit: null,
        };

    const ownerValues = {
      ...data.owner,
      individual: individualValues,
      organisation: organisationValues,
      legalTypes: legalTypesKeys,
    };

    const { legalEntityId, primaryAddress, ...documentInformationValues } =
      data.documentInformation as any;

    return {
      general: generalValues,
      owner: ownerValues,
      conditions: conditionsValues,
      documentInformation: documentInformationValues,
    };
  };

  const addLicenceTemplateApplicationProcess = async (
    data: ILicenceTemplateApplicationProcess,
  ) => {
    try {
      const response = await createLicenceTemplateApplicationProcess(data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_APPLICATION_PROCESS_ALERT, {
          text: t('licence.applicationProcess.add.success.text'),
        });
      }
      return response.data?.uniqueId;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const editLicenceTemplateApplicationProcessPrice = async (
    uniqueId: string,
    body: ILicenceTemplateApplicationProcessPriceUpdate,
  ) => {
    try {
      const response = await editLicenceTemplateApplicationProcessPriceData(uniqueId, body);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licence.applicationProcess.editPrice.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const editLicenceTemplateApplicationProcessSettings = async (
    uniqueId: string,
    body: ILicenceTemplateApplicationProcessSettingsUpdate,
  ) => {
    try {
      const response = await editLicenceTemplateApplicationProcessSettingsData(uniqueId, body);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licence.price.edit.successSettings.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const handleDeleteLicencePrice = async (id: string) => {
    const response = await deleteLicenceApplicationProcess(id);
    if (response) {
      await setAlert(ActionTypes.SET_APPLICATION_PROCESS_ALERT, {
        text: t('applicationProcess.delete.success.text'),
      });
    }
    return response;
  };
  const handleActivateApplicationProccess = async (id: string) => {
    try {
      const response = await activateApplicationProccess(id);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('applicationProcess.activate.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };
  const handleDeactivateApplicationProccess = async (id: string) => {
    try {
      const response = await deactivateApplicationProccess(id);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('applicationProcess.deactivate.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const addLicenceTemplate = async (data: ILicenceTemplateFormValues) => {
    try {
      const formData = { ...prepareFormDataToSave(data) };
      const response = await createLicenceTemplate(formData);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licence.add.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const editLicenceTemplate = async (
    uniqueId: string,
    version: string,
    data: ILicenceTemplateFormValues,
  ) => {
    try {
      const formData = { uniqueId, version, ...prepareFormDataToSave(data) };
      const response = await editLicenceTemplateData(formData);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licence.edit.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const handleDelete = async (id: string) => {
    const response = await deleteLicence(id);
    if (response) {
      await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
        text: t('licence.delete.success.text'),
      });
    }
    return response;
  };
  const handleChangeStatus = async (id: string, isActiveStatus: boolean) => {
    const response = await changeLicenceTemplateStatus(id, isActiveStatus);
    if (response) {
      await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: isActiveStatus
          ? t('licence.unpublished.success.text')
          : t('licence.published.success.text'),
      });
    }
    return response;
  };

  const addApplicationProccessTask = async (
    data: ILicenceTemplateApplicationProcessTaskRequest,
  ) => {
    try {
      const response = await createApplicationProccessTask(data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_TASK_ALERT, {
          text: t('licence.addTask.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_TASK_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const handleDeleteApplicationProccessTask = async (id: string) => {
    const response = await deleteApplicationProccessTask(id);
    if (response) {
      await setAlert(ActionTypes.SET_TASK_ALERT, {
        text: t('licence.deleteTask.success.text'),
      });
    }
    return response;
  };

  const handleGetapplicationProccessTask = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ): Promise<ILicenceTemplateApplicationProcessTask | null> => {
    const data = await getApplicationProccessTask(
      id,
      currentPage,
      perPage,
      sort ?? {
        property: 'createdDate',
        direction: ESortDirectionTypes.DESC,
      },
    );
    if (data) {
      const { content, ...paginatorData } = data;
      setApplicationProccessTasks(content);
      setPaginatorApplicationProccessTasks(paginatorData);
    } else {
      setApplicationProccessTasks([]);
      setPaginatorApplicationProccessTasks(null);
    }
    return null;
  };

  const addApplicationProccessDocument = async (
    data: ILicenceTemplateApplicationProcessDocumentRequest,
  ) => {
    try {
      const response = await createApplicationProccessDocument(data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_DOCUMENT_ALERT, {
          text: t('licence.addDocument.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_DOCUMENT_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const handleDeleteApplicationProccessDocument = async (id: string) => {
    const response = await deleteApplicationProccessDocument(id);
    if (response) {
      await setAlert(ActionTypes.SET_DOCUMENT_ALERT, {
        text: t('licence.deleteDocument.success.text'),
      });
    }
    return response;
  };

  const handleGetApplicationProccessDocument = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ): Promise<ILicenceTemplateApplicationProcessDocument | null> => {
    const data = await getApplicationProccessDocument(
      id,
      currentPage,
      perPage,
      sort ?? {
        property: 'createdDate',
        direction: ESortDirectionTypes.DESC,
      },
    );
    if (data) {
      const { content, ...paginatorData } = data;
      setApplicationProccessDocuments(content);
      setPaginatorApplicationProccessDocuments(paginatorData);
    } else {
      setApplicationProccessDocuments([]);
      setPaginatorApplicationProccessDocuments(null);
    }
    return null;
  };

  const addApplicationProccessIntelligenceReport = async (
    data: ILicenceTemplateApplicationProcessIntelligenceReportRequest,
  ) => {
    try {
      const response = await createApplicationProccessIntelligenceReport(data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_INTELLIGENCE_REPORT_ALERT, {
          text: t('licence.addIntelligenceReport.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_INTELLIGENCE_REPORT_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };
  const editApplicationProccessIntelligenceReport = async (
    id: string,
    data: ILicenceTemplateApplicationProcessIntelligenceReportForm,
  ) => {
    try {
      const response = await updateApplicationProccessIntelligenceReport(id, data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_INTELLIGENCE_REPORT_ALERT, {
          text: t('licence.updateIntelligenceReport.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_INTELLIGENCE_REPORT_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const editPrintoutSettings = async (id: string, data: IPrintoutSettings) => {
    try {
      const response = await updatePrintoutSettings(id, data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licence.updatePrintoutSettings.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };
  const addPrintoutSettings = async (id: string, data: IPrintoutSettings) => {
    try {
      const response = await createPrintoutSettings(id, data);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('licence.addPrintoutSettings.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const handleDeleteApplicationProccessIntelligenceReport = async (id: string) => {
    const response = await deleteApplicationProccessIntelligenceReport(id);
    if (response) {
      await setAlert(ActionTypes.SET_INTELLIGENCE_REPORT_ALERT, {
        text: t('licence.deleteIntelligenceReport.success.text'),
      });
    }
    return response;
  };

  const handleGetapplicationProccessIntelligenceReport = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ): Promise<ILicenceTemplateApplicationProcessIntelligenceReport | null> => {
    const data = await getApplicationProccessIntelligenceReport(
      id,
      currentPage,
      perPage,
      sort ?? {
        property: 'createdDate',
        direction: ESortDirectionTypes.DESC,
      },
    );
    if (data) {
      const { content, ...paginatorData } = data;
      setApplicationProccessIntelligenceReports(content);
      setPaginatorApplicationProccessIntelligenceReports(paginatorData);
    } else {
      setApplicationProccessIntelligenceReports([]);
      setPaginatorApplicationProccessIntelligenceReports(null);
    }
    return null;
  };

  const preparePrintoutSettingsFields = async (id: string) => {
    const data = await getPrintoutSettingsAvailableFields(id);
    if (data) {
      setGeneralFields(
        printoutSettingsFieldFilter(data, printoutSettingsGeneralFields, 'general'),
      );
      setConditionsFields(
        printoutSettingsFieldFilter(data, printoutSettingsConditionsFields, 'conditions'),
      );
      setOwnerFields(printoutSettingsFieldFilter(data, printoutSettingsOwnerFields, 'owner'));
      setWeaponDataFields(
        printoutSettingsFieldFilter(data, printoutSettingsWeaponDataFields, 'weaponData'),
      );
    }
  };

  const loadDictionaries = async (types: EDictionaryTypes[]) => {
    if (types.includes(EDictionaryTypes.weaponLicenceCategory)) {
      getWeaponLicenceCategory();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceCompetency)) {
      getWeaponLicenceCompetency();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceGoodReason)) {
      getWeaponLicenceGoodReason();
    }
    if (types.includes(EDictionaryTypes.weaponLicencePaymentStatus)) {
      getWeaponLicencePaymentStatus();
    }
    if (types.includes(EDictionaryTypes.weaponLicencingAuthority)) {
      getWeaponLicencingAuthority();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceStatus)) {
      getWeaponLicenceStatus();
    }
    if (types.includes(EDictionaryTypes.Region)) {
      getRegion();
    }
    if (types.includes(EDictionaryTypes.Country)) {
      getCountry();
    }
    if (types.includes(EDictionaryTypes.AddressType)) {
      getAddressType();
    }
    if (types.includes(EDictionaryTypes.OrganisationCategory)) {
      getOrganisationCategory();
    }
    if (types.includes(EDictionaryTypes.OrganisationCivilianType)) {
      getOrganisationCivilianType();
    }
    if (types.includes(EDictionaryTypes.OrganisationStateType)) {
      getOrganisationStateType();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceCondition)) {
      getWeaponLicenceCondition();
    }
    if (types.includes(EDictionaryTypes.attachmentDocumentTypeData)) {
      getAttachmentDocumentTypeData();
    }
    if (types.includes(EDictionaryTypes.applicationProcessTypes)) {
      getApplicationProcessTypes();
    }
    if (types.includes(EDictionaryTypes.OrganisationMilitaryType)) {
      getOrganisationMilitaryType();
    }
    if (types.includes(EDictionaryTypes.OrganisationPoliceType)) {
      getOrganisationPoliceType();
    }
    if (types.includes(EDictionaryTypes.applicationProcessStatus)) {
      getApplicationProcessStatus();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceTemplateStatus)) {
      getWeaponLicenceTemplateStatus();
    }
    if (types.includes(EDictionaryTypes.legalType)) {
      getLegalType();
    }
    if (types.includes(EDictionaryTypes.task)) {
      getTask();
    }
    if (types.includes(EDictionaryTypes.otherRequestTaskName)) {
      getOtherRequestTaskName();
    }
    if (types.includes(EDictionaryTypes.weaponLicenceCaseStatus)) {
      getWeaponLicenceCaseStatus();
    }
    if (types.includes(EDictionaryTypes.intelligenceReportSourceSystem)) {
      getIntelligenceReportSourceSystem();
    }
  };

  return {
    paginator,
    licence,
    getLicence,
    licences,
    getLicencesData,
    addLicenceTemplate,
    editLicenceTemplate,
    handleDelete,
    loadDictionaries,
    weaponLicenceCategory,
    weaponLicenceCompetency,
    weaponLicenceGoodReason,
    applicationProcessTypes,
    weaponLicencePaymentStatus,
    weaponLicenceStatus,
    weaponLicencingAuthority,
    region,
    country,
    addressType,
    organisationCategory,
    organisationCivilianType,
    organisationPoliceType,
    organisationMilitaryType,
    organisationStateType,
    weaponLicenceCondition,
    applicationProcessStatus,
    attachmentDocumentTypeData,
    task,
    otherRequestTaskName,
    handleChangeStatus,
    getLicenceApplicationProccessData,
    licencePrices,
    addLicenceTemplateApplicationProcess,
    editLicenceTemplateApplicationProcessPrice,
    editLicenceTemplateApplicationProcessSettings,
    getLicenceApplicationProcessTypes,
    licenceProcessTypes,
    getLicenceApplicationProccessDetail,
    handleActivateApplicationProccess,
    handleDeactivateApplicationProccess,
    handleDeleteLicencePrice,
    licenceApplicationProccess,
    addApplicationProccessTask,
    handleDeleteApplicationProccessTask,
    handleGetapplicationProccessTask,
    applicationProccessTasks,
    paginatorApplicationProccessTasks,
    addApplicationProccessDocument,
    handleGetApplicationProccessDocument,
    applicationProccessDocuments,
    handleDeleteApplicationProccessDocument,
    paginatorApplicationProccessDocuments,
    addApplicationProccessIntelligenceReport,
    handleDeleteApplicationProccessIntelligenceReport,
    handleGetapplicationProccessIntelligenceReport,
    editApplicationProccessIntelligenceReport,
    applicationProccessIntelligenceReports,
    paginatorApplicationProccessIntelligenceReports,
    weaponLicenceTemplateStatus,
    legalType,
    weaponLicenceCaseStatus,
    intelligenceReportSourceSystem,
    editPrintoutSettings,
    addPrintoutSettings,
    preparePrintoutSettingsFields,
    generalFields,
    conditionsFields,
    ownerFields,
    weaponDataFields,
  };
};
